import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL != null ? process.env.REACT_APP_API_URL : 'http://localhost:8000';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WEEKDAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function TdeeSection() {
  const [tdeeData, setTdeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState(30);
  const [goalKgPerWeek, setGoalKgPerWeek] = useState(0.5);

  useEffect(() => {
    loadTdee();
  }, [window]);

  const loadTdee = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/analytics/tdee`, { params: { window_days: window } });
      setTdeeData(res.data);
    } catch {
      setTdeeData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="tdee-loading"><div className="spinner"></div>Berechne TDEE...</div>;

  const current = tdeeData?.current;
  const hasError = !current || tdeeData?.error;

  const confidenceLabel = { high: 'Hohe', medium: 'Mittlere', low: 'Niedrige' };
  const confidenceColor = { high: 'var(--success-600, #16a34a)', medium: 'var(--warning-500, #f59e0b)', low: 'var(--error-500, #ef4444)' };

  // History chart
  const history = tdeeData?.history || [];
  const maxTdee = history.length ? Math.max(...history.map(h => h.real_deficit_per_day ?? 0)) : 0;
  const minTdee = history.length ? Math.min(...history.map(h => h.real_deficit_per_day ?? 0)) : 0;

  return (
    <div className="analysis-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Defizit-Realitätscheck</h3>
        <select
          value={window}
          onChange={e => setWindow(Number(e.target.value))}
          style={{ padding: '0.35rem 0.6rem', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--card-bg, white)', color: 'var(--text-primary, #1f2937)', fontSize: '0.875rem', cursor: 'pointer' }}
        >
          <option value={14}>14 Tage</option>
          <option value={30}>30 Tage</option>
          <option value={60}>60 Tage</option>
          <option value={90}>90 Tage</option>
        </select>
      </div>
      <p className="section-info">
        Tatsächlicher Energieverbrauch – berechnet aus deiner Gewichtsveränderung und Kalorienprotokoll, unabhängig von Formeln.
      </p>

      {hasError ? (
        <div style={{ color: 'var(--text-secondary, #6c757d)', padding: '1rem 0' }}>
          {tdeeData?.error || 'Nicht genügend Daten. Mindestens 7 Tage mit Gewicht und Kalorien nötig.'}
        </div>
      ) : (
        <>
          {/* Defizit-Realitätscheck */}
          {(() => {
            const real = current.real_deficit_per_day;
            const assumed = current.assumed_deficit_per_day;
            const hasComparison = real != null && assumed != null;
            // Positive = deficit (losing weight), Negative = surplus (gaining)
            const gap = hasComparison ? assumed - real : null;
            const gapPct = hasComparison && real !== 0 ? Math.round((gap / Math.abs(real)) * 100) : null;
            const state = !hasComparison ? 'nodata'
              : real <= 0 ? 'gaining'
              : gap > Math.abs(real) * 0.2 ? 'over'
              : gap < -Math.abs(real) * 0.2 ? 'under'
              : 'accurate';
            const stateConfig = {
              over:     { color: 'var(--warning-500, #f59e0b)', bg: 'var(--warning-50, #fffbeb)', icon: '⚠', label: `${gapPct ?? 0}% Überschätzung`, msg: 'Entweder isst du mehr als eingetragen oder Garmin überschätzt deinen Verbrauch.' },
              under:    { color: 'var(--primary-500, #3b82f6)', bg: 'var(--primary-50, #eff6ff)', icon: '↑', label: `${Math.abs(gapPct ?? 0)}% Unterschätzung`, msg: 'Du verbrennst mehr als erwartet — oder isst weniger als Tracking zeigt.' },
              accurate: { color: 'var(--success-600, #16a34a)', bg: 'var(--success-50, #f0fdf4)', icon: '✓', label: 'Tracking stimmt', msg: 'Deine Gewichtsentwicklung passt gut zu deinem Kalorienprotokoll.' },
              gaining:  { color: 'var(--text-muted, #9ca3af)', bg: 'var(--bg-color, #f9fafb)', icon: '↗', label: 'Gewichtszunahme', msg: 'Im gewählten Zeitraum zeigt die Kurve eine Gewichtszunahme.' },
              nodata:   { color: 'var(--text-muted, #9ca3af)', bg: 'var(--bg-color, #f9fafb)', icon: '–', label: 'Kein Vergleich', msg: 'Keine Garmin-Aktivitätsdaten zum Vergleich vorhanden.' },
            };
            const cfg = stateConfig[state];
            return (
              <div style={{ background: cfg.bg, borderRadius: 'var(--radius-lg, 12px)', padding: '1.25rem', marginBottom: '1rem', borderLeft: `4px solid ${cfg.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: '0.5rem' }}>Defizit-Realitätscheck</div>
                    {hasComparison && real > 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)', fontWeight: 600 }}>Tracking sagt</div>
                          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary, #1f2937)' }}>−{Math.abs(assumed).toLocaleString('de-DE')} kcal/T</div>
                        </div>
                        <div style={{ fontSize: '1.25rem', color: 'var(--text-muted, #9ca3af)' }}>→</div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)', fontWeight: 600 }}>Körper zeigt</div>
                          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: cfg.color }}>−{Math.abs(real).toLocaleString('de-DE')} kcal/T</div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: cfg.color, marginBottom: '0.25rem' }}>
                        {real != null ? `Trend: ${real > 0 ? '−' : '+'}${Math.abs(real).toLocaleString('de-DE')} kcal/T` : 'Nicht genug Daten'}
                      </div>
                    )}
                    <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: cfg.color, fontWeight: 600 }}>{cfg.icon} {cfg.label}</div>
                    <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: 'var(--text-secondary, #6c757d)' }}>{cfg.msg}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', minWidth: '160px' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Details</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #9ca3af)' }}>TDEE ~</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{current.estimated_tdee.toLocaleString('de-DE')} kcal</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #9ca3af)' }}>Ø gegessen</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{current.avg_consumed.toLocaleString('de-DE')} kcal</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #9ca3af)' }}>Trend</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{current.slope_kg_per_day > 0 ? '+' : ''}{current.slope_kg_per_day} kg/T</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #9ca3af)' }}>Konfidenz</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: confidenceColor[current.confidence] }}>{confidenceLabel[current.confidence]}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #9ca3af)' }}>Zeitraum</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted, #9ca3af)' }}>{current.period_start} – {current.period_end}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Recommendations */}
          {(() => {
            const deficitPerDay = Math.round(goalKgPerWeek * 7700 / 7);
            const goalKcal = current.estimated_tdee - deficitPerDay;
            return (
              <div className="tdee-recommendations">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <h4 style={{ margin: 0 }}>Zielkalorien</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-muted, #9ca3af)', fontWeight: 600 }}>Ziel:</label>
                    <select
                      value={goalKgPerWeek}
                      onChange={e => setGoalKgPerWeek(Number(e.target.value))}
                      style={{ padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--card-bg, white)', color: 'var(--text-primary, #1f2937)', fontSize: '0.8rem', cursor: 'pointer' }}
                    >
                      <option value={0.25}>0,25 kg/Woche</option>
                      <option value={0.5}>0,5 kg/Woche</option>
                      <option value={0.75}>0,75 kg/Woche</option>
                      <option value={1.0}>1,0 kg/Woche</option>
                    </select>
                  </div>
                </div>
                <div className="tdee-goals-grid">
                  {[
                    { label: 'Gewicht halten', kcal: current.estimated_tdee, color: 'var(--primary-500, #3b82f6)' },
                    { label: `${String(goalKgPerWeek).replace('.', ',')} kg/Woche`, kcal: goalKcal, color: goalKcal < 1200 ? 'var(--error-500, #ef4444)' : 'var(--success-600, #16a34a)' },
                    { label: `Defizit/Tag`, kcal: deficitPerDay, color: 'var(--warning-500, #f59e0b)', suffix: ' kcal' },
                  ].map(g => (
                    <div key={g.label} className="tdee-goal-item">
                      <div className="tdee-goal-kcal" style={{ color: g.color }}>{g.kcal.toLocaleString('de-DE')}{g.suffix || ''}</div>
                      <div className="tdee-goal-label">{g.label}</div>
                    </div>
                  ))}
                </div>
                {goalKcal < 1200 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--error-500, #ef4444)', fontWeight: 600 }}>
                    ⚠ Unter 1.200 kcal – zu aggressives Defizit
                  </div>
                )}
              </div>
            );
          })()}

          {/* History Chart */}
          {history.length >= 2 && (
            <div className="tdee-history">
              <h4>Echtes Defizit im Verlauf</h4>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted, #9ca3af)', marginBottom: '0.75rem' }}>
                Tatsächliches Defizit pro Segment aus der Gewichtskurve abgeleitet.
              </p>
              <div className="tdee-history-chart">
                {history.map((seg, idx) => {
                  const heightPct = maxTdee > minTdee
                    ? 20 + (((seg.real_deficit_per_day ?? 0) - minTdee) / (maxTdee - minTdee)) * 75
                    : 60;
                  const label = seg.period_end.slice(0, 7); // YYYY-MM
                  return (
                    <div key={idx} className="tdee-hist-bar-wrap" title={`${label}: −${seg.real_deficit_per_day ?? '?'} kcal/T (${seg.tracking_days} Tage)`}>
                      <div className="tdee-hist-bar-value">{(seg.real_deficit_per_day ?? 0).toLocaleString('de-DE')}</div>
                      <div className="tdee-hist-bar" style={{ height: `${heightPct}%` }} />
                      <div className="tdee-hist-label">{label.slice(5)}/{label.slice(2, 4)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)', marginTop: '0.75rem' }}>
            Formel: TDEE = Ø Kalorien + (Regressions-Slope × 7.700) · Gewichts-Trendlinie über alle Messpunkte im Zeitraum.
          </div>
        </>
      )}

      <style>{`
        .tdee-loading { padding: 1.5rem 0; display: flex; align-items: center; gap: 0.5rem; color: var(--text-secondary, #6c757d); }
        .tdee-recommendations {
          background: var(--primary-50, #eff6ff);
          border-radius: var(--radius-lg, 12px);
          padding: 1rem 1.25rem;
          margin-bottom: 1rem;
        }
        .tdee-recommendations h4 { margin: 0 0 0.75rem; font-weight: 600; color: var(--text-primary, #1f2937); }
        .tdee-goals-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem; }
        @media (max-width: 420px) { .tdee-goals-grid { grid-template-columns: 1fr 1fr; } }
        .tdee-goal-item { text-align: center; background: var(--card-bg, white); border-radius: var(--radius-md, 8px); padding: 0.75rem 0.5rem; }
        .tdee-goal-kcal { font-size: 1.25rem; font-weight: 700; }
        .tdee-goal-label { font-size: 0.72rem; color: var(--text-muted, #9ca3af); margin-top: 0.2rem; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; }
        .tdee-history h4 { margin: 0 0 0.25rem; font-weight: 600; }
        .tdee-history-chart {
          display: flex;
          align-items: flex-end;
          gap: 0.4rem;
          height: 120px;
          padding-bottom: 1.5rem;
          position: relative;
          overflow-x: auto;
        }
        .tdee-hist-bar-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          flex: 1;
          min-width: 40px;
          height: 100%;
          position: relative;
          cursor: default;
        }
        .tdee-hist-bar-value {
          font-size: 0.65rem;
          font-weight: 600;
          color: var(--text-muted, #9ca3af);
          margin-bottom: 3px;
          white-space: nowrap;
        }
        .tdee-hist-bar {
          width: 100%;
          background: var(--primary-400, #60a5fa);
          border-radius: 4px 4px 0 0;
          transition: height 0.3s ease;
          min-height: 8px;
        }
        .tdee-hist-bar-wrap:hover .tdee-hist-bar { background: var(--primary-500, #3b82f6); }
        .tdee-hist-label {
          position: absolute;
          bottom: -1.25rem;
          font-size: 0.65rem;
          color: var(--text-muted, #9ca3af);
          font-weight: 600;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
}

function AnalyticsPage() {
  const [weekdayData, setWeekdayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(90);

  useEffect(() => {
    loadWeekdayAnalysis();
  }, [period]);

  const loadWeekdayAnalysis = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/api/analytics/weekday-analysis`, {
        params: { days: period }
      });
      setWeekdayData(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getBarWidth = (value, max) => {
    if (!value || !max) return 0;
    return Math.round((value / max) * 100);
  };

  const getBarColor = (value, avg, type) => {
    if (!value || !avg) return 'var(--text-muted, #9ca3af)';
    const diff = ((value - avg) / avg) * 100;

    if (type === 'calories') {
      // Mehr Kalorien = rot, weniger = grün
      if (diff > 10) return 'var(--error-500, #ef4444)';
      if (diff > 5) return 'var(--warning-500, #f59e0b)';
      if (diff < -10) return 'var(--success-600, #16a34a)';
      if (diff < -5) return 'var(--primary-400, #60a5fa)';
      return 'var(--text-muted, #9ca3af)';
    } else {
      // Mehr Defizit = grün, weniger = rot
      if (diff > 10) return 'var(--success-600, #16a34a)';
      if (diff > 5) return 'var(--primary-400, #60a5fa)';
      if (diff < -10) return 'var(--error-500, #ef4444)';
      if (diff < -5) return 'var(--warning-500, #f59e0b)';
      return 'var(--text-muted, #9ca3af)';
    }
  };

  if (loading) return <div className="loading"><div className="spinner"></div>Lade Analyse...</div>;
  if (error) return <div className="error">Fehler: {error}</div>;

  const weekdays = weekdayData?.weekdays || [];
  const maxCalories = weekdays.length ? Math.max(...weekdays.map(d => d.avg_calories || 0)) : 0;
  const maxDeficit = weekdays.length ? Math.max(...weekdays.map(d => d.avg_deficit || 0)) : 0;

  return (
    <div className="analytics-page">
      <h2>Analysen</h2>

      <div className="period-selector">
        <label>Zeitraum: </label>
        <select value={period} onChange={(e) => setPeriod(Number(e.target.value))}>
          <option value={30}>30 Tage</option>
          <option value={90}>90 Tage</option>
          <option value={180}>6 Monate</option>
          <option value={365}>1 Jahr</option>
          <option value={0}>Alle Daten</option>
        </select>
      </div>

      {/* Wochentag-Analyse */}
      <div className="analysis-section">
        <h3>Wochentag-Analyse</h3>
        <p className="section-info">
          Wie unterscheidet sich dein Essverhalten je nach Wochentag?
        </p>

        {weekdayData && (
          <>
            <div className="weekday-summary">
              <div className="summary-item">
                <span className="label">Analyse-Zeitraum:</span>
                <span className="value">{weekdayData.days_analyzed} Tage</span>
              </div>
              <div className="summary-item">
                <span className="label">Ø Kalorien gesamt:</span>
                <span className="value">{weekdayData.overall_avg_calories} kcal</span>
              </div>
              <div className="summary-item">
                <span className="label">Ø Defizit gesamt:</span>
                <span className="value">{weekdayData.overall_avg_deficit} kcal</span>
              </div>
            </div>

            <div className="weekday-charts">
              {/* Kalorien Chart */}
              <div className="chart-section">
                <h4>Ø Kalorien pro Wochentag</h4>
                <div className="bar-chart">
                  {weekdays.map((day, idx) => (
                    <div key={idx} className="bar-row">
                      <span className="bar-label">{WEEKDAYS[idx]}</span>
                      <div className="bar-container">
                        <div
                          className="bar"
                          style={{
                            width: `${getBarWidth(day.avg_calories, maxCalories)}%`,
                            backgroundColor: getBarColor(day.avg_calories, weekdayData.overall_avg_calories, 'calories')
                          }}
                        />
                        <span className="bar-value">{day.avg_calories || '-'}</span>
                      </div>
                      <span className="bar-diff">
                        {day.avg_calories && weekdayData.overall_avg_calories
                          ? `${day.avg_calories > weekdayData.overall_avg_calories ? '+' : ''}${day.avg_calories - weekdayData.overall_avg_calories}`
                          : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Defizit Chart */}
              <div className="chart-section">
                <h4>Ø Defizit pro Wochentag</h4>
                <div className="bar-chart">
                  {weekdays.map((day, idx) => (
                    <div key={idx} className="bar-row">
                      <span className="bar-label">{WEEKDAYS[idx]}</span>
                      <div className="bar-container">
                        <div
                          className="bar"
                          style={{
                            width: `${getBarWidth(day.avg_deficit, maxDeficit)}%`,
                            backgroundColor: getBarColor(day.avg_deficit, weekdayData.overall_avg_deficit, 'deficit')
                          }}
                        />
                        <span className="bar-value">{day.avg_deficit || '-'}</span>
                      </div>
                      <span className="bar-diff">
                        {day.avg_deficit && weekdayData.overall_avg_deficit
                          ? `${day.avg_deficit > weekdayData.overall_avg_deficit ? '+' : ''}${day.avg_deficit - weekdayData.overall_avg_deficit}`
                          : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Wochenende vs Wochentag */}
            <div className="weekend-comparison">
              <h4>Wochenende vs. Wochentag</h4>
              <div className="comparison-grid">
                <div className="comparison-item">
                  <span className="comp-label">Mo-Fr Ø Kalorien</span>
                  <span className="comp-value">{weekdayData.weekday_avg_calories} kcal</span>
                </div>
                <div className="comparison-item">
                  <span className="comp-label">Sa-So Ø Kalorien</span>
                  <span className="comp-value">{weekdayData.weekend_avg_calories} kcal</span>
                </div>
                <div className="comparison-item diff">
                  <span className="comp-label">Unterschied</span>
                  <span className={`comp-value ${weekdayData.weekend_diff_calories > 0 ? 'negative' : 'positive'}`}>
                    {weekdayData.weekend_diff_calories > 0 ? '+' : ''}{weekdayData.weekend_diff_calories} kcal
                  </span>
                </div>
                <div className="comparison-item">
                  <span className="comp-label">Mo-Fr Ø Defizit</span>
                  <span className="comp-value">{weekdayData.weekday_avg_deficit} kcal</span>
                </div>
                <div className="comparison-item">
                  <span className="comp-label">Sa-So Ø Defizit</span>
                  <span className="comp-value">{weekdayData.weekend_avg_deficit} kcal</span>
                </div>
                <div className="comparison-item diff">
                  <span className="comp-label">Unterschied</span>
                  <span className={`comp-value ${weekdayData.weekend_diff_deficit < 0 ? 'negative' : 'positive'}`}>
                    {weekdayData.weekend_diff_deficit > 0 ? '+' : ''}{weekdayData.weekend_diff_deficit} kcal
                  </span>
                </div>
              </div>
            </div>

            {/* Insights */}
            {weekdayData.insights && weekdayData.insights.length > 0 && (
              <div className="weekday-insights">
                <h4>Erkenntnisse</h4>
                <ul>
                  {weekdayData.insights.map((insight, idx) => (
                    <li key={idx}>{insight}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {/* TDEE */}
      <TdeeSection />

      <style>{`
        .analytics-page {
          padding: var(--space-4, 1rem);
          max-width: 1200px;
          margin: 0 auto;
        }

        h2 {
          margin-bottom: var(--space-4, 1rem);
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        .period-selector {
          margin-bottom: var(--space-6, 1.5rem);
          display: flex;
          align-items: center;
          gap: var(--space-3, 0.75rem);
        }

        .period-selector label {
          font-weight: 500;
          color: var(--text-secondary, #6c757d);
        }

        .period-selector select {
          padding: var(--space-2, 0.5rem) var(--space-3, 0.75rem);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-md, 8px);
          background-color: var(--card-bg, white);
          color: var(--text-primary, #1f2937);
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .period-selector select:hover {
          border-color: var(--border-hover, #d1d5db);
        }

        .period-selector select:focus {
          border-color: var(--primary-500, #3b82f6);
          box-shadow: 0 0 0 3px var(--primary-100, #dbeafe);
          outline: none;
        }

        .analysis-section {
          background: var(--card-bg, white);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-6, 1.5rem);
          margin-bottom: var(--space-6, 1.5rem);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
        }

        .analysis-section h3 {
          margin: 0 0 var(--space-2, 0.5rem) 0;
          font-weight: 600;
        }

        .section-info {
          color: var(--text-secondary, #6c757d);
          margin-bottom: var(--space-4, 1rem);
        }

        .weekday-summary {
          display: flex;
          gap: var(--space-8, 2rem);
          margin-bottom: var(--space-6, 1.5rem);
          padding-bottom: var(--space-4, 1rem);
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          flex-wrap: wrap;
        }

        .summary-item {
          display: flex;
          flex-direction: column;
        }

        .summary-item .label {
          font-size: 0.75rem;
          color: var(--text-muted, #9ca3af);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 600;
        }

        .summary-item .value {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--text-primary, #1f2937);
        }

        .weekday-charts {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: var(--space-8, 2rem);
          margin-bottom: var(--space-6, 1.5rem);
        }

        .chart-section h4 {
          margin: 0 0 var(--space-4, 1rem) 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .bar-chart {
          display: flex;
          flex-direction: column;
          gap: var(--space-2, 0.5rem);
        }

        .bar-row {
          display: flex;
          align-items: center;
          gap: var(--space-2, 0.5rem);
        }

        .bar-label {
          width: 30px;
          font-weight: 600;
          font-size: 0.9rem;
          color: var(--text-primary, #1f2937);
        }

        .bar-container {
          flex: 1;
          height: 32px;
          background: var(--bg-color, #f9fafb);
          border-radius: var(--radius-md, 8px);
          position: relative;
          overflow: hidden;
        }

        .bar {
          height: 100%;
          border-radius: var(--radius-md, 8px);
          transition: width 0.3s ease;
        }

        .bar-value {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-primary, #1f2937);
        }

        .bar-diff {
          width: 55px;
          font-size: 0.8rem;
          color: var(--text-muted, #9ca3af);
          text-align: right;
          font-weight: 500;
        }

        .weekend-comparison {
          background: var(--bg-color, #f9fafb);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-5, 1.25rem);
          margin-bottom: var(--space-4, 1rem);
        }

        .weekend-comparison h4 {
          margin: 0 0 var(--space-4, 1rem) 0;
          font-weight: 600;
        }

        .comparison-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-4, 1rem);
        }

        @media (max-width: 600px) {
          .comparison-grid {
            grid-template-columns: 1fr 1fr;
          }
          .comparison-item.diff {
            grid-column: span 2;
          }
        }

        .comparison-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--space-4, 1rem);
          background: var(--card-bg, white);
          border-radius: var(--radius-md, 8px);
          transition: transform 0.2s;
        }

        .comparison-item:hover {
          transform: translateY(-2px);
        }

        .comparison-item.diff {
          background: var(--primary-50, #eff6ff);
        }

        .comp-label {
          font-size: 0.75rem;
          color: var(--text-muted, #9ca3af);
          margin-bottom: var(--space-1, 0.25rem);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 600;
        }

        .comp-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--text-primary, #1f2937);
        }

        .comp-value.positive {
          color: var(--success-600, #16a34a);
        }

        .comp-value.negative {
          color: var(--error-500, #ef4444);
        }

        .weekday-insights {
          background: var(--warning-50, #fffbeb);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-5, 1.25rem);
          border-left: 4px solid var(--warning-500, #f59e0b);
        }

        .weekday-insights h4 {
          margin: 0 0 var(--space-2, 0.5rem) 0;
          color: var(--warning-700, #b45309);
          font-weight: 600;
        }

        .weekday-insights ul {
          margin: 0;
          padding-left: var(--space-6, 1.5rem);
        }

        .weekday-insights li {
          margin-bottom: var(--space-1, 0.25rem);
          color: var(--warning-700, #b45309);
        }

        .loading, .error {
          padding: var(--space-8, 2rem);
          text-align: center;
        }

        .error {
          color: var(--error-500, #ef4444);
        }
      `}</style>
    </div>
  );
}

export default AnalyticsPage;
